import _ from 'lodash'
import * as chartColors from 'scoreboard/charts/config/chart-colors'
import i18n from 'scoreboard/components/i18n'
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'
import BootslideMenu from 'scoreboard/components/bootslide/bootslide-menu'
import AddDomainObjectEndpoint from 'scoreboard/components/bootslide/add-domain-object-endpoint'
import { sectionEndpoint }  from 'scoreboard/components/bootslide/object-selector-menu'
import buildDatasetFieldSelectorMenu from 'scoreboard/components/bootslide/build-dataset-field-selector-menu'
import Dataset from 'scoreboard/datasets/model/dataset'
import ScorecardNode from 'scoreboard/scorecards/scorecard-node'
import unwrapModel from 'scoreboard/components/unwrap-model'
import { axisDataTypeInfo } from 'scoreboard/dashboards/widgets/chart/config/y-axis-range-config'
import { adjustYAxisSettings } from 'scoreboard/charts/model/chart-designer'
import option from 'scoreboard/components/bootslide/option.hbs'
import ChartDesignerSerie from '../../model/chart-designer-serie'
import adjustBreakdownDatasetFields from './adjust-breakdown-dataset-fields'
import plotTypes from './plot-types'
import createScorecardBreakdown from './create-scorecard-breakdown'
import ensureSeriePeriods from './ensure-serie-periods'
const MENU_WIDTH = 200
import license from 'scoreboard/components/license'
import Backbone from 'backbone'
import Tooltip from '../../../components/tooltip'
import ChartScorecardItemEndpoint from './chart-scorecard-item-endpoint'

const AddSerieMenu = BootslideMenu.extend({

	/**
	 * @type ChartDesigner
	 */
	model: null,

	plotType: null,

	/**
	 * @param {String} options.axis
	 */
	initialize: function(options = {}) {
		BootslideMenu.prototype.initialize.apply(this, arguments)
		this.plotType = this.model.get('plotType')
	},

	menu: function () {
		let target
		const allowedPlotTypes = this._allowedPlotTypes()
		if (allowedPlotTypes.length > 1) {
			target = this.plotTypeTargets()
		} else {
			// If we can only add 1 type of plot, don't display the plot menu and skip
			// straight to the data source menu
			const plotType = allowedPlotTypes[0]
			target = this._allowedDomainTypeTargets(plotType)
		}

		return {
			label: i18n.get('charts.addDataSeries'),
			target,
			width: MENU_WIDTH
		}
	},

	plotTypeTargets: function () {
		let toPlotTypeTarget  = (plotType, label, icon) => {
			return {
				label,
				content: _.constant(option({
					icon: icon || plotType,
					label
				})),
				target: this._allowedDomainTypeTargets(plotType),
				width: MENU_WIDTH
			}
		}

		const allowedPlotTypes = this._allowedPlotTypes()
		return plotTypes()
			.filter(p => allowedPlotTypes.includes(p.type))
			.map(type => toPlotTypeTarget(type.type, type.label, type.icon))
	},

	_allowedPlotTypes: function() {
		const plotType = plotTypes().find(p => p.type === this.plotType)
		if (!plotType) {
			// #39240 If the plot type is null, an error will be thrown anyway, and
			// needs to be. This is just so we have a more descriptive error in case
			// someone needs to fix this besides me
			throw new Error('The chart designer is missing a plot type. Cannot add new data to chart')
		}
		return plotType.canAdd
	},

	_allowedDomainTypeTargets: function(plotType) {
		const targets = []
		const oldAxisInfo = axisDataTypeInfo(this.model, this.model.data)
		if (this.model.breakdowns.toArray().length > 1) {
			// #46035 scorecard/initiative series can't be broken down by multiple
			// date fields, so these options should be disabled
			targets.push(this._disabledDomainTypeTarget('scorecard',
				'scorecards.scorecardNode', 'charts.chart.cannotAddScorecard'))
			targets.push(this._disabledDomainTypeTarget('initiative',
				'initiatives.initiative', 'charts.chart.cannotAddInitiative'))
		} else {
			targets.push(this.scorecardsTarget(plotType, { oldAxisInfo }))
			targets.push(this.initiativesTarget(plotType, { oldAxisInfo }))
		}

		// #42905 only show the "Datasets" option if allowed by license
		if (license.datasets) {
			targets.push(this.datasetTarget(plotType, { oldAxisInfo }))
		}
		return targets
	},

	_disabledDomainTypeTarget: function(domainType, labeli18n, messagei18n) {
		const label = i18n.get(labeli18n)
		return {
			label,
			contentView: new DomainTypeTargetDisabled({
				label,
				icon: domainType,
				message: i18n.get(messagei18n)
			})
		}
	},

	scorecardsTarget: function (plotType, opts={}) {
		let menu = this
		let AddScorecardOption = ChartScorecardItemEndpoint.extend({
			add: function (node, org) {
				// There aren't any options in the tree endpoint to return the
				// aggregation type, so instead of adding yet another option to
				// it, just fetch the selected scorecard item so we have everything
				// that we need
				menu.fetchScorecardItem(node.id).then(sn => {
					menu.addScorecardSerie(sn, plotType)

					const newAxisInfo = axisDataTypeInfo(this.model, this.model.data)
					adjustYAxisSettings(opts.oldAxisInfo, newAxisInfo, this.model)
				})
			}
		})

		let label = 'scorecards.scorecardNode'
		return {
			label: i18n.get(label),
			content: _.constant(option({
				icon: 'scorecard',
				label
			})),
			contentEndpoint: sectionEndpoint(menu, 'scorecards', AddScorecardOption)
		}
	},

	addScorecardSerie: function (sn, plotType) {
		let serieAttrs = this.commonSerieAttrs({ plotType })

		_.extend(serieAttrs, {
			labelType: 'domain-object-label',
			source: {
				domainType: 'scorecard-item',
				scorecardItemId: sn.id,
				dataProperty: getDefaultDataProperty(sn, plotType)
			}
		})

		let newSerie = new ChartDesignerSerie(serieAttrs)
		this.addNewSerie(newSerie)

		// #26468 if there's a scorecard serie, there must be a
		// calendar period breakdown, so if there isn't already a
		// breakdown, automatically add a calendar period breakdown.
		// If there is already at least one breakdown, just leave it
		// alone; the user will need to figure out what should be
		// changed.
		createScorecardBreakdown(this.model)

		ensureSeriePeriods({ chartDesigner: this.model, newSerie })
	},

	fetchScorecardItem: function (id) {
		let sn = this.model.data.scorecards(id)
		if (sn) {
			return Promise.resolve(sn)
		}
		return new Promise(resolve => new ScorecardNode({ id }).fetch({
			success: model => {
				const node = unwrapModel(model)
				this.model.data.scorecards(id, node)
				resolve(node)
			}
		}))
	},

	initiativesTarget: function (plotType, opts={}) {
		let menu = this
		let AddInitiativeOption = AddDomainObjectEndpoint.extend({
			add: function (init, org) {
				if (!this.model.data.initiatives(init.id)) {
					this.model.data.initiatives(init.id, init)
				}

				let serieAttrs = menu.commonSerieAttrs({ plotType })

				_.extend(serieAttrs, {
					labelType: 'domain-object-label',
					source: {
						domainType: 'initiative',
						initiativeId: init.id,
						dataProperty: 'percent-complete'
					}
				})

				let newSerie = new ChartDesignerSerie(serieAttrs)
				menu.addNewSerie(newSerie)
				ensureSeriePeriods({ chartDesigner: this.model, newSerie })

				const newAxisInfo = axisDataTypeInfo(this.model, this.model.data)
				adjustYAxisSettings(opts.oldAxisInfo, newAxisInfo, this.model)
			}
		})

		let label = 'initiatives.initiative'
		return {
			label: i18n.get(label),
			content: _.constant(option({
					icon: 'initiative',
					label
				})),
			contentEndpoint: sectionEndpoint(menu, 'initiatives', AddInitiativeOption)
		}
	},

	datasetTarget: function (plotType, opts={}) {
		return buildDatasetFieldSelectorMenu({
			menuLabel: 'datasets.dataset',
			iconId: 'dataset',
			bootslideMenu: this,
			fieldSelectorTargetId: `dataset-chart-${plotType}-field`,
			fieldSelectorOptions: {
				allowDateField: false,
				allowTotal: true
			},
			onFieldSelect: selected => {
				this.addSerie({ selected, plotType })

				const newAxisInfo = axisDataTypeInfo(this.model, this.model.data)
				adjustYAxisSettings(opts.oldAxisInfo, newAxisInfo, this.model)
			}
		})
	},

	addSerie: function ({ selected, plotType }) {
		let serieAttrs = this.commonSerieAttrs({ plotType })

		let aggregationType = selected.field.aggregationType
		if (!aggregationType) {
			aggregationType = Dataset.isTotal(selected.field.id) || isNumericDataType(selected.field.dataType)
				// #35636 the total field (ie Number of X) and any numeric fields
				// without a default aggregationType defined (usually they should
				// have one) should default to SUM
				? 'sum'
				// #18514 non-numeric fields won't have an aggregationType; in that case,
				// we should default to 'unique'
				: 'unique'
		}

		_.extend(serieAttrs, {
			source: {
				domainType: 'dataset',
				aggregation: {
					startingDatasetId: selected.dataset.id,
					fieldId: selected.field.id,
					visibleLinkedFieldId: selected.visibleLinkedFieldId,
					aggregationType,
					dataSourceType: 'basic',
					filters: []
				}
			}
		})

		// #42155 This needs to be available before rendering the panels
		this.model.data.datasets(selected.dataset.id, unwrapModel(selected.dataset))
		this.addNewSerie(new ChartDesignerSerie(serieAttrs))

		adjustBreakdownDatasetFields(this.model)
	},

	commonSerieAttrs: function ({ plotType }) {
		return {
			type: 'serie',
			tempId: _.uniqueId(ChartDesignerSerie.TEMP_ID_PREFIX),
			plotType,
			color: this.nextColor(),
			axis: this.options.axis,
			sortOrder: 0
		}
	},

	nextColor: function () {
		let usedColors = this.model.features.map(f => f.get('color'))
		return chartColors.nextPrioritized(usedColors)
	},

	/**
	 * Adds a new serie to the list of features for the axis, adjusting all sort
	 * orders as necessary (because serie go to the top of the list)
	 */
	addNewSerie: function(newSerie) {
		const axisFeatures = this.model.featuresByAxis(this.options.axis)
		this.model.addFeature(newSerie, null, axisFeatures, 0)
		this.model.sortFeatures(this.options.axis, axisFeatures)
	}
})

const DomainTypeTargetDisabled = Backbone.View.extend({
	className: 'domain-type-menu-option is-disabled',

	events: {
		'click': 'showTooltip',
		'mouseleave': 'removeTooltip'
	},

	initialize: function(options) {
		this.options = options
	},

	render: function() {
		const content = option({
			icon: this.options.icon,
			label: this.options.label
		})
		this.$el.empty().html(content)
		return this
	},

	showTooltip: function() {
		this.tooltip = new Tooltip({
			// attach to the parent so the tooltip shows completely outside of the menu
			attachTo: this.$el.parent(),
			content: this.options.message,
			closeOnChangedFocus: true,
			closeOnAttachedToClick: true,
			placement: 'right',
			arrow: 'bottom'
		}).render()
	},

	removeTooltip: function() {
		_.invoke(this.tooltip, 'remove')
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.tooltip], 'remove')
	}
})

const getDefaultDataProperty = (sn, plotType) => {
	if (ScorecardNode.metricish(sn) &&
		!_.includes(['filled-container', 'waffle', 'funnel'], plotType)) {
		return 'value'
	}
	return 'score'
}

export default AddSerieMenu
export { getDefaultDataProperty }

AddSerieMenu.__filename__ = 'charts/edit/serie/add-serie-menu.js'
